import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { PaginatedStaffReferral, StaffUser } from "../../../../model/referral/referral";
import { formatTimestamp, formatCurrency } from "../../../../utils";
import { FilterIcon2, SearchIcon } from "../../../../assets/icons";
import Button from "../../../../common/components/xepp/button/Button";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import styles from "../ReferralSystem.module.scss";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import { DateRangePicker, Range } from "react-date-range";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import { SystemReferralMenuIconActive } from "../../../../assets/sidemenu-icons";

interface RegisterUserTableProps {
    registerUsers: PaginatedStaffReferral<StaffUser> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    page: number;
    search?: string;
    fromDate?: Date;
    toDate?: Date;
    onPageChange: (page: number) => void;
    onSearchChange: (val: string) => void;
    onDateFilterChange:(from: Date, to: Date) => void;
    onDateFilterReset: () => void;

}

const PAGE_SIZE = 10;
const RegisterUserTable = ({registerUsers, isLoading, isFetching, page, search, fromDate, toDate, onPageChange, onSearchChange, onDateFilterChange, onDateFilterReset}: RegisterUserTableProps) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [dateRange, setDateRange] = useState<Range>({
        startDate: undefined,
        endDate: undefined,
        key: "selection",
    });
    const [appliedFilters, setAppliedFilters] = useState<{
        from?: Date;
        to?: Date;
    }>({});

   


    const maxDate = new Date();


    const handleApplyFilter = () => {
        if(dateRange.startDate && dateRange.endDate)
            onDateFilterChange(dateRange.startDate, dateRange.endDate)
        setIsFilterOpen(false);
        console.log(dateRange.startDate, dateRange.endDate)
    };

    const handleResetFilter = () => {
        setDateRange({ startDate: undefined, endDate: undefined, key: "selection" });
        onDateFilterReset();
        setIsFilterOpen(false);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value);
    };

    const columns: Array<ColumnType<StaffUser>> = [
        {
            accessor: "createdAt",
            header: "Date & Time",
            render: (record: StaffUser) => <span>{formatTimestamp(record.signupDate)}</span>,
        },
        {
            accessor: "name",
            header: "Name of Customer",
            render: (record: StaffUser) => <span>{record.name}</span>,
        },
        {
            accessor: "email",
            header: "Email Address",
            render: (record: StaffUser) => <span>{record.email}</span>,
        },
        {
            accessor: "staffId",
            header: "Phone Number",
            render: (record: StaffUser) => <span>{record.phoneNumber.internationalNumber}</span>,
        },
        {
            accessor: "revenue",
            header: "Referral Transaction",
            render: (record: StaffUser) => <span>{formatCurrency(record.revenue ?? 0)}</span>,
        },
    ];

    const headerActions = (
        <div className={styles.headerActions}>
            <SearchIcon />
            <input
                className={styles.searchInput}
                type="text"
                placeholder="Search by Name"
                value={search}
                onChange={handleSearchChange}
            />
            <div className={styles.filterActions}>
                <span className={styles.filterStatus}>Date</span>
                {(appliedFilters.from || appliedFilters.to) && (
                    <>

                        <Button text="Clear" variant="text" onClick={handleResetFilter} />
                    </>
                )}
                <Button
                    leftIcon={<FilterIcon2 />}
                    text="Add Filter"
                    variant="outline"
                    type="button"
                    onClick={() => setIsFilterOpen(true)}
                />
            </div>
        </div>
    );

    return (
        <>
            {isFilterOpen && (
                <CommonDialog noPadding>
                    <div className={styles.dialogContainer}>
                        <div className={styles.dialogHeader}>
                            <h2 className={styles.dialogTitle}>Select Date Range</h2>
                            <button className={styles.closeButton} onClick={() => setIsFilterOpen(false)}>
                                ×
                            </button>
                        </div>
                        <div className={styles.dateRangeWrapper}>
                            <DateRangePicker
                                ranges={[dateRange]}
                                onChange={(ranges: any) => setDateRange(ranges.selection)}
                                moveRangeOnFirstSelection={false}
                                months={1}
                                direction="horizontal"
                                maxDate={maxDate}
                                showDateDisplay={false}
                                rangeColors={["#42577A"]}
                            />
                        </div>
                        <div className={styles.dialogActions}>
                            <Button text="Reset" variant="outline" onClick={handleResetFilter} />
                            <Button text="Apply Filter" onClick={handleApplyFilter} />
                        </div>
                    </div>
                </CommonDialog>
            )}

            <DataTable<StaffUser>
                data={registerUsers?.items ?? []}
                columns={columns}
                loading={isLoading}
                isUpdating={isFetching && !isLoading}
                headerActions={headerActions}
                emptyComponent={
                    <EmptyState
                        icon={<SystemReferralMenuIconActive />}
                        title="No customers found"
                    />
                }
            />

            <TablePagination
                isLoading={isFetching}
                totalItems={registerUsers?.count ?? 0}
                itemsPerPage={PAGE_SIZE}
                currentPage={page}
                onPageChange={onPageChange}
            />
        </>
    );
}

export default RegisterUserTable;